import { useEffect, useRef, useState } from "react";
import { useObservedElementRef } from "./useObservedElementRef";

export interface UseSubtreeMeasureOptions {
  /**
   * Set false to stop observing and leave the value at `initialValue`. Lets a caller measure only on
   * the code path that needs it without calling the hook conditionally.
   */
  enabled?: boolean;
}

export interface UseSubtreeMeasureResult<V, T extends HTMLElement = HTMLElement> {
  /**
   * Attach to the element whose subtree should be measured, when this component owns that element.
   * Ignore it when passing a container ref in instead.
   */
  containerRef: (element: T | null) => void;
  /** The latest measurement, or `initialValue` before the first one and while disabled. */
  value: V;
}

/**
 * Keeps a measurement of a container's **subtree layout** up to date: `measure` runs on mount, again
 * whenever any child of the container resizes, and again whenever children are added or removed.
 *
 * Use this instead of {@link useResizeObserver} when what you are measuring depends on the container's
 * *contents* rather than on the container's own size — for example how far one descendant sits below
 * the top of a scroll container. Observing the container alone is the intuitive approach and does not
 * work: a height-capped, scrolling container never resizes when content grows inside it, because only
 * its `scrollHeight` changes. Its children have to be observed instead.
 *
 * Watching `childList` is the other half, and equally load-bearing. Children observed once at mount
 * are a snapshot, so anything that renders later — a chart that appears when its data arrives, the
 * classic case — would move the layout without ever triggering a re-measure. The hook re-subscribes
 * whenever the child set changes.
 *
 * The container can be supplied two ways. Pass `undefined` and attach the returned `containerRef` when
 * this component renders the element itself. Or pass the element directly when measuring a scroll
 * container that an ancestor owns — the typical case — which is what
 * {@link useObservedElementRef} is for on that ancestor's side: it tracks its node as state, so the
 * element arrives here as a value that changes when the node is swapped.
 *
 * A `RefObject` is deliberately not accepted for the external path. Mutating `.current` notifies
 * nothing, so a consumer given only a ref can notice an attach solely by re-reading it every render —
 * a dependency-less effect running for every consumer on every pass, plus an extra commit on mount.
 * Taking the element as a prop pushes that tracking to the one component that actually owns the node.
 *
 * `measure` is held in a ref, so it need not be memoized, and it closes over whatever else the
 * measurement needs.
 *
 * Note this observes only direct children. A resize nested deeper that does not change any child's
 * own box will not be caught.
 *
 * @param externalContainer - an element owned elsewhere, or `undefined`/`null` to attach the returned
 *   `containerRef` instead; nothing happens while no element is attached
 * @param measure - derives the value from the container, against live layout
 * @param initialValue - value before the first measurement, and while `enabled` is false
 *
 * @example
 * ```tsx
 * // Measuring a scroll container an ancestor owns: how far the list sits below the top of its content.
 * // The ancestor tracks its own node with `useObservedElementRef` and passes the element down.
 * const { value: startMargin } = useSubtreeMeasure(
 *   scrollElement,
 *   (container) => {
 *     const list = listRef.current;
 *     if (!list) return 0;
 *     const offset = list.getBoundingClientRect().top - container.getBoundingClientRect().top;
 *     return Math.max(0, Math.round(offset + container.scrollTop));
 *   },
 *   0,
 *   { enabled: hasExternalScroller }
 * );
 * ```
 *
 * @example
 * ```tsx
 * // Measuring an element this component owns.
 * const { containerRef, value: contentHeight } = useSubtreeMeasure(
 *   undefined,
 *   (container) => container.scrollHeight,
 *   0
 * );
 * return <div ref={containerRef}>{children}</div>;
 * ```
 */
export const useSubtreeMeasure = <V, T extends HTMLElement = HTMLElement>(
  externalContainer: T | null | undefined,
  measure: (container: T) => V,
  initialValue: V,
  options: UseSubtreeMeasureOptions = {}
): UseSubtreeMeasureResult<V, T> => {
  const { enabled = true } = options;
  const measureRef = useRef(measure);
  measureRef.current = measure;
  const [value, setValue] = useState<V>(initialValue);

  /**
   * Ref for callers that own the measured node themselves. React invokes it with the element on attach
   * and `null` on detach, so an element swap (`<button>` to `<div>`) re-binds naturally.
   */
  const { ref: containerRefCallback, element: attachedContainer } = useObservedElementRef<T>();

  // A caller using the returned ref passes no external element at all, so the attached node wins. Both
  // paths arrive as values that change on a swap, so the effect below re-subscribes without anything
  // re-reading a ref during render.
  const container = attachedContainer ?? externalContainer ?? null;

  useEffect(() => {
    if (!container || !enabled) return;

    const remeasure = () => setValue(measureRef.current(container));

    const resizeObserver = new ResizeObserver(remeasure);
    const observeChildren = () => {
      resizeObserver.disconnect();
      Array.from(container.children).forEach((child) => resizeObserver.observe(child));
      remeasure();
    };
    observeChildren();

    const mutationObserver = new MutationObserver(observeChildren);
    mutationObserver.observe(container, { childList: true });

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [container, enabled]);

  return { containerRef: containerRefCallback, value: enabled ? value : initialValue };
};
